// ============================================================================
// Admin API Service
// src/services/adminApi.js
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.hashvi.in';

// ── Admin Authentication ──

export const adminLogin = async (username, password) => {
  const response = await fetch(`${API_BASE}/admin/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ username, password })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Login failed');
  }
  
  return response.json();
};

export const getAdminProfile = async () => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/me`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to get profile');
  return response.json();
};

export const changeAdminPassword = async (currentPassword, newPassword) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/change-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Password change failed');
  }
  
  return response.json();
};

// ── Admin: Agreements Management ──

export const adminGetAllAgreements = async (filters = {}) => {
  const token = localStorage.getItem('admin_token');
  
  const params = new URLSearchParams();
  if (filters.status_filter) params.append('status_filter', filters.status_filter);
  if (filters.landlord_id) params.append('landlord_id', filters.landlord_id);
  if (filters.payment_status) params.append('payment_status', filters.payment_status);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const response = await fetch(`${API_BASE}/admin/agreements?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to fetch agreements');
  return response.json();
};

export const adminUpdateDeliveryStatus = async (agreementId, statusData) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/agreements/${agreementId}/status`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(statusData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Update failed');
  }
  
  return response.json();
};

// ── Admin: Landlords Management ──

export const adminGetAllLandlords = async (filters = {}) => {
  const token = localStorage.getItem('admin_token');
  
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.is_approved !== undefined) params.append('is_approved', filters.is_approved);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const response = await fetch(`${API_BASE}/admin/landlords?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to fetch landlords');
  return response.json();
};

export const adminGetLandlordDetails = async (landlordId) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/landlords/${landlordId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to fetch landlord details');
  return response.json();
};

export const adminUpdateLandlord = async (landlordId, updateData) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/landlords/${landlordId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updateData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Update failed');
  }
  
  return response.json();
};

// ── Admin: Statistics ──

export const adminGetStatistics = async () => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/stats`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to fetch statistics');
  return response.json();
};

// ── Admin: Audit Logs ──

export const adminGetAuditLogs = async (filters = {}) => {
  const token = localStorage.getItem('admin_token');
  
  const params = new URLSearchParams();
  if (filters.admin_id) params.append('admin_id', filters.admin_id);
  if (filters.action) params.append('action', filters.action);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const response = await fetch(`${API_BASE}/admin/audit-logs?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) throw new Error('Failed to fetch audit logs');
  return response.json();
};

// ── Helper: Download Agreement PDF ──

export const adminDownloadAgreementPDF = async (agreementId, tenantName) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${API_BASE}/admin/agreements/${agreementId}/download`, {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    redirect: 'follow'  // ✅ Follow redirects to Cloudinary
  });
  
  if (!response.ok) throw new Error('Failed to download PDF');
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Agreement_${tenantName}_${new Date().getFullYear()}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
